import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "./auth-context";
import apiClient from "../utils/api-client";
import {
  DisplayFormat,
  getDisplayFormat,
  setDisplayFormat,
} from "../utils/formatter";

interface DisplayFormatContextValue {
  format: DisplayFormat;
  setFormat: (format: DisplayFormat) => void;
}

const DisplayFormatContext = React.createContext<DisplayFormatContextValue>({
  format: "eu",
  setFormat: () => {},
});

function isDisplayFormat(value: unknown): value is DisplayFormat {
  return value === "eu" || value === "us";
}

function DisplayFormatProvider(props: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [format, setFormatState] = useState<DisplayFormat>(getDisplayFormat());

  useEffect(() => {
    if (!user) {
      return;
    }
    let cancelled = false;
    (async () => {
      const response = await apiClient.getDisplayFormat();
      if (cancelled || !isDisplayFormat(response?.display_format)) {
        return;
      }
      setDisplayFormat(response.display_format);
      setFormatState(response.display_format);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const setFormat = (next: DisplayFormat) => {
    setDisplayFormat(next);
    setFormatState(next);
  };

  return (
    <DisplayFormatContext.Provider value={{ format, setFormat }} {...props} />
  );
}

function useDisplayFormat() {
  return useContext(DisplayFormatContext);
}

export { DisplayFormatProvider, useDisplayFormat };
